import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import type { MappingRow, MappingError, MappingSortConfig, MappingStatus } from "@/types";
import { STATUS_SORT_ORDER } from "@/lib/schema";

/**
 * Core state management for the column mapping workflow.
 *
 * Direction-agnostic: status updates trigger on any meaningful
 * field change (sourceColumn or targetFieldId), regardless of
 * the row's direction property.
 *
 * Validates duplicate targets AND duplicate sources globally.
 */
export function useMappingState(
  initialRows: MappingRow[],
  getFieldName: (id: string) => string = () => ""
) {
  const [rows, setRows] = useState<MappingRow[]>(initialRows);
  const [sort, setSort] = useState<MappingSortConfig>({ field: null, direction: "asc" });

  // ── Row Mutations ────────────────────────────────────────

  const updateRow = useCallback((id: string, patch: Partial<MappingRow>) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const updated = { ...r, ...patch };

        // Direction-agnostic status logic:
        // If a meaningful field was just set, upgrade "needs_match" to "mapped_by_user"
        const targetChanged = patch.targetFieldId !== undefined && patch.targetFieldId !== "";
        const sourceChanged = patch.sourceColumn !== undefined && patch.sourceColumn !== "";

        if ((targetChanged || sourceChanged) && updated.status === "needs_match") {
          updated.status = "mapped_by_user";
        }

        // If a meaningful field was cleared, downgrade to "needs_match"
        if (patch.targetFieldId === "" || patch.sourceColumn === "") {
          // Only reset to needs_match if the row is now incomplete
          const isComplete = !!updated.sourceColumn && !!updated.targetFieldId;
          if (!isComplete) {
            updated.status = "needs_match";
            updated.approved = false;
            updated.confidence = 0;
          }
        }

        return updated;
      })
    );
  }, []);

  const toggleApproval = useCallback(
    (id: string, blockedIds?: Set<string>, protectedIds?: Set<string>) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          // A row is approvable when it has BOTH source and target
          const hasValue = !!r.sourceColumn && !!r.targetFieldId;
          if (!hasValue && !r.approved) return r;
          // Block approving rows that have errors
          if (!r.approved && blockedIds?.has(r.id)) return r;
          // Prevent unapproving protected (mandatory) rows
          if (r.approved && protectedIds?.has(r.id)) return r;
          return { ...r, approved: !r.approved };
        })
      );
    },
    []
  );

  const approveAll = useCallback((excludeIds?: Set<string>) => {
    setRows((prev) =>
      prev.map((r) => {
        if (excludeIds?.has(r.id)) return r;
        const hasValue = !!r.sourceColumn && !!r.targetFieldId;
        return hasValue ? { ...r, approved: true } : r;
      })
    );
  }, []);

  const deselectAll = useCallback((protectedIds?: Set<string>) => {
    setRows((prev) =>
      prev.map((r) => {
        if (protectedIds?.has(r.id)) return r;
        return { ...r, approved: false };
      })
    );
  }, []);

  const appendRows = useCallback((newRows: MappingRow[]) => {
    setRows((prev) => [...prev, ...newRows]);
  }, []);

  /**
   * Merges additional-mapping rows into the state.
   * - If an existing row has the same sourceColumn but no targetFieldId yet
   *   (orphaned needs_match from the mandatory pass), it is replaced.
   * - target_to_source rows (non-mandatory targets with no source match) are appended.
   * - Any row whose sourceColumn already has a complete mapping is skipped.
   */
  const mergeAdditionalRows = useCallback((newRows: MappingRow[]) => {
    setRows((prev) => {
      const bySource = new Map<string, number>();
      prev.forEach((r, i) => {
        if (r.sourceColumn) bySource.set(r.sourceColumn, i);
      });

      const result = [...prev];
      for (const newRow of newRows) {
        if (!newRow.sourceColumn) {
          result.push(newRow);
          continue;
        }
        const idx = bySource.get(newRow.sourceColumn);
        if (idx === undefined) {
          result.push(newRow);
          bySource.set(newRow.sourceColumn, result.length - 1);
        } else {
          const existing = result[idx];
          if (existing && !existing.targetFieldId) {
            result[idx] = newRow;
          }
        }
        // existing row already has a target — skip to avoid duplicate
      }
      return result;
    });
  }, []);

  // ── Global Validation (across all rows) ────────────────────

  const errors = useMemo<MappingError[]>(() => {
    const errs: MappingError[] = [];

    // Duplicate targets — only count rows with BOTH source and target filled
    const targetCounts = new Map<string, string[]>();
    for (const r of rows) {
      if (!r.targetFieldId || !r.sourceColumn) continue;
      const existing = targetCounts.get(r.targetFieldId) ?? [];
      existing.push(r.id);
      targetCounts.set(r.targetFieldId, existing);
    }
    for (const [fieldId, ids] of targetCounts) {
      if (ids.length > 1) {
        const name = getFieldName(fieldId);
        errs.push({
          type: "duplicate_target",
          rowIds: ids,
          value: name,
          message: `"${name}" is assigned to ${ids.length} different source columns`,
        });
      }
    }

    // Duplicate sources — only count rows with BOTH source and target filled
    const sourceCounts = new Map<string, string[]>();
    for (const r of rows) {
      if (!r.sourceColumn || !r.targetFieldId) continue;
      const existing = sourceCounts.get(r.sourceColumn) ?? [];
      existing.push(r.id);
      sourceCounts.set(r.sourceColumn, existing);
    }
    for (const [col, ids] of sourceCounts) {
      if (ids.length > 1) {
        errs.push({
          type: "duplicate_source",
          rowIds: ids,
          value: col,
          message: `"${col}" is used as a source for ${ids.length} different target fields`,
        });
      }
    }

    return errs;
  }, [rows, getFieldName]);

  /** Set of row IDs that have errors — includes ALL rows in each error group */
  const errorRowIds = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    for (const e of errors) {
      for (const id of e.rowIds) ids.add(id);
    }
    return ids;
  }, [errors]);

  /** Map from row ID to its error messages (a row can have multiple errors) */
  const errorsByRowId = useMemo<Map<string, string[]>>(() => {
    const map = new Map<string, string[]>();
    for (const e of errors) {
      for (const id of e.rowIds) {
        const existing = map.get(id) ?? [];
        existing.push(e.message);
        map.set(id, existing);
      }
    }
    return map;
  }, [errors]);

  /** Set of target field IDs currently assigned */
  const assignedTargetIds = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    for (const r of rows) {
      if (r.targetFieldId) ids.add(r.targetFieldId);
    }
    return ids;
  }, [rows]);

  // ── Auto-deselect rows that gain errors ────────────────────
  const errorRowIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const prev = errorRowIdsRef.current;
    errorRowIdsRef.current = errorRowIds;
    let hasNew = false;
    for (const id of errorRowIds) {
      if (!prev.has(id)) {
        hasNew = true;
        break;
      }
    }
    if (!hasNew) return;
    setRows((r) =>
      r.map((row) => (row.approved && errorRowIds.has(row.id) ? { ...row, approved: false } : row))
    );
  }, [errorRowIds]);

  // ── Sorting ──────────────────────────────────────────────

  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    const { field, direction } = sort;
    const dir = direction === "asc" ? 1 : -1;

    sorted.sort((a, b) => {
      let cmp = 0;
      switch (field) {
        case "sourceColumn":
          cmp = (a.sourceColumn || "\uffff").localeCompare(b.sourceColumn || "\uffff");
          break;
        case "targetColumn": {
          const nameA = getFieldName(a.targetFieldId) || "\uffff";
          const nameB = getFieldName(b.targetFieldId) || "\uffff";
          cmp = nameA.localeCompare(nameB);
          break;
        }
        case "status":
          cmp = STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status];
          break;
        case "confidence":
          cmp = a.confidence - b.confidence;
          break;
      }
      return cmp * dir;
    });
    return sorted;
  }, [rows, sort, getFieldName]);

  // ── Stats ────────────────────────────────────────────────

  const stats = useMemo(() => {
    const total = rows.length;
    const approved = rows.filter((r) => r.approved).length;
    const approvable = rows.filter((r) => {
      return !!r.sourceColumn && !!r.targetFieldId;
    }).length;
    const assigned = approvable;
    const byStatus = rows.reduce(
      (acc, r) => {
        acc[r.status] = (acc[r.status] ?? 0) + 1;
        return acc;
      },
      {} as Record<MappingStatus, number>
    );
    return { total, approved, assigned, approvable, byStatus };
  }, [rows]);

  return {
    rows: sortedRows,
    rawRows: rows,
    sort,
    setSort,
    updateRow,
    toggleApproval,
    approveAll,
    deselectAll,
    appendRows,
    mergeAdditionalRows,
    errors,
    errorRowIds,
    errorsByRowId,
    assignedTargetIds,
    stats,
  };
}
